/* eslint-disable react/require-default-props */
import React from 'react';
import gql from 'graphql-tag';

import {
  Item2FlagListingData,
  Item2FlagListingMeData,
  core_apimessages_UserStatus,
} from '../schemas/rql';
import { LISTING_GUIDELINES_URL, LISTING_STATES, FLAG_LISTING_REASONS } from './shared/constants';
import experiments from '@reverbdotcom/commons/src/experiments';
import { withPrefix } from '../lib/i18n_helpers';

import { IUser } from '@reverbdotcom/commons/src/components/user_context_provider';
import { useUser } from '@reverbdotcom/commons/src/user_hooks';
import FormGroup from '@reverbdotcom/commons/src/components/form_group';
import FormGroupWithRadio from '@reverbdotcom/commons/src/components/form_group_with_radio';
import FormGroupWithInput from '@reverbdotcom/commons/src/components/form_group_with_input';
import CsrfForm from '@reverbdotcom/commons/src/csrf_form';
import SanitizedRender from '@reverbdotcom/commons/src/components/sanitized_render';
import { isExperimentEnabled, isOwner } from '@reverbdotcom/commons/src/user_context_helpers';
import { RCButton, RCTextWithIcon } from '@reverbdotcom/cadence/components';
import { FlagIcon } from '@reverbdotcom/cadence/icons/react';
import { Modal } from '@reverbdotcom/commons';

interface IProps {
  user: Item2FlagListingMeData.Fragment;
  listing: Item2FlagListingData.Fragment;
  id?: string;
}

const t = withPrefix('discovery.flagListing');

export const Item2FlagListingFragment = gql`
  fragment Item2FlagListingData on Listing {
    _id
    id
    state
    sellerId
  }
`;

export const Item2FlagListingMeFragment = gql`
  fragment Item2FlagListingMeData on rql_Me {
    uuid
    status
  }
`;

function userAccountInGoodStanding(status: string) {
  return status === core_apimessages_UserStatus.IN_GOOD_STANDING;
}

function hideFlagListingButton(status: string, user: Partial<IUser>, listing: Item2FlagListingData.Fragment) {
  if (isExperimentEnabled(user, experiments.BLOCKED_FROM_FLAGGING_LISTINGS)) { return true; }
  if (isOwner(user, listing)) { return true; }
  if (listing.state !== LISTING_STATES.LIVE) { return true; }

  if (user.isAdmin) { return false; }
  if (userAccountInGoodStanding(status)) { return false; }

  return true;
}

function violatesGuidelinesField(id: string) {
  return (
    <FormGroupWithRadio
      label={
        <SanitizedRender
          html={t('violatesListingGuidelines', {
            linkOpen: `<a href="${LISTING_GUIDELINES_URL}" target="_blank">`,
            linkClose: '</a>',
          })}
        />
      }
      inputName="reason"
      id={`reason_${FLAG_LISTING_REASONS.VIOLATES_GUIDELINES}_${id}`}
      value={FLAG_LISTING_REASONS.VIOLATES_GUIDELINES}
      required
    />
  );
}

function adminFlagListingForm(listing: Item2FlagListingData.Fragment, id: string) {
  return (
    <CsrfForm
      action={`/listings/${listing.id}/flag`}
      method="post"
      data-disable-auto-validation="true"
      data-admin-flag-listing-form
    >
      <FormGroup
        label={t('reportReason')}
        tagRequired
      >
        {violatesGuidelinesField(id)}
      </FormGroup>

      <FormGroupWithInput
        label={t('adminReason')}
        placeholder={t('descriptionPlaceholder')}
        inputName="description"
        id={`description_${id}`}
        maxLength={500}
        tagRequired
        inputType="textarea"
      />

      <RCButton
        variant="primary"
        isSubmit
      >
        {t('submitReport')}
      </RCButton>
    </CsrfForm>
  );
}

function flagListingForm(listing: Item2FlagListingData.Fragment, toggleForm: (val: boolean) => void, id: string) {
  return (
    <CsrfForm
      action={`/listings/${listing.id}/flag`}
      method="post"
      data-disable-auto-validation="true"
      data-flag-listing-form
    >
      <p>{t('formDescription')}</p>
      <FormGroup
        label={t('reportReason')}
        tagRequired
      >
        {violatesGuidelinesField(id)}
        <FormGroupWithRadio
          label={t('fraudulent')}
          inputName="reason"
          value={FLAG_LISTING_REASONS.FRAUDULENT}
          id={`reason_${FLAG_LISTING_REASONS.FRAUDULENT}_${id}`}
          required
        />
        <FormGroupWithRadio
          label={t('violatesTrademark')}
          inputName="reason"
          value={FLAG_LISTING_REASONS.TRADEMARK_INFRINGEMENT}
          id={`reason_${FLAG_LISTING_REASONS.TRADEMARK_INFRINGEMENT}_${id}`}
          required
        />
      </FormGroup>

      <FormGroupWithInput
        label={t('descriptionHeader')}
        placeholder={t('descriptionPlaceholder')}
        inputName="description"
        id={`description_${id}`}
        helpText={t('descriptionMaxLength')}
        maxLength={500}
        tagRequired
        required
        inputType="textarea"
      />

      <RCButton
        variant="primary"
        isSubmit
      >
        {t('submitReport')}
      </RCButton>

      <span className="ml-1">
        <RCButton
          variant="tertiary"
          onClick={() => toggleForm(false)}
        >
          {t('collapseForm')}
        </RCButton>
      </span>
    </CsrfForm>
  );
}

export function Item2FlagListing(props: IProps) {
  const [form, toggleForm] = React.useState(false);
  const userContext = useUser();

  if (!props.user || !props.listing) {
    return null;
  }

  if (hideFlagListingButton(
    props.user.status,
    userContext,
    props.listing,
  )) {
    return null;
  }

  return (
    <div className="item2__sidebar-module">
      <RCButton
        size="small"
        variant="tertiary"
        onClick={() => toggleForm(!form)}
      >
        <RCTextWithIcon
          placement="left"
          svgComponent={FlagIcon}
        >
          {t('reportItem')}
        </RCTextWithIcon>
      </RCButton>

      <Modal
        isOpen={form}
        onRequestClose={() => toggleForm(!form)}
        headerTitle={t('reportItem')}
        paddedBody
        shouldCloseOnOverlayClick
      >
        {userContext.isAdmin ? adminFlagListingForm(props.listing, props.id) : flagListingForm(props.listing, toggleForm, props.id)}
      </Modal>
    </div>
  );
}
